import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';

export interface ConfirmOptions {
  /** A question the person answers: "Archive Website redesign?" */
  title: string;
  /** What happens, and what stays: "Its tasks and history stay." */
  body?: ReactNode;
  /** Says what happens: "Archive", "Remove", "Leave task". */
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` for anything that removes, ends or cannot be undone. */
  tone?: 'danger' | 'primary';
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<Confirm | null>(null);

/**
 * One confirmation dialog for the whole app, asked for in a line:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Remove Design?', confirmLabel: 'Remove', tone: 'danger' }))) return;
 *
 * Built on the shadcn AlertDialog (Base UI), so it stacks above Base UI sheets
 * and dialogs. Focus starts on Cancel and an outside click does not answer.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Kept after closing so the text does not vanish during the exit animation.
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>(
    (next) =>
      new Promise<boolean>((resolve) => {
        resolver.current?.(false);
        resolver.current = resolve;
        setOptions(next);
        setOpen(true);
      }),
    [],
  );

  const answer = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpen(false);
  };

  return (
    <Ctx.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(next) => !next && answer(false)}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title}</AlertDialogTitle>
            {options?.body && <AlertDialogDescription>{options.body}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{options?.cancelLabel ?? 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              variant={options?.tone === 'danger' ? 'destructive' : 'default'}
              onClick={() => answer(true)}
            >
              {options?.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export function useConfirm(): Confirm {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
}
